// src/AppRouter.js
import React, { useEffect, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { isTokenExpired } from './utils/services/Auth/isTokenExpired';
import { AuthContext } from "./context/AuthContext/AuthContext";
// Token stays in localStorage for API compatibility
import LoadingSpinner from './components/Advanced/Feedback/LoadingSpinner';
import { PatientModalProvider } from './context/PatientModalContext/PatientModalContext';
import { ObservationsModalProvider } from './context/ObservationsModalContext/ObservationsModalContext';

// Páginas
import { LoginPage as Login, ChangePasswordPage } from './modules/login-v2';
// import Main from './pages/Main/MainPage'; // Used via mainRoutes
import { mainRoutes } from "./modules/Main";
import { carePlansRoutes } from './modules/care-plans';
import { assignmentRoutes } from './modules/assignments';
import UserProfilePage from './pages/UserProfile/UserProfilePage';
import MfaRequiredPage from './pages/MfaRequired/MfaRequiredPage';
import { PatientProfilesPage } from './modules/care-management-system/patient-profiles';
import { clinicalInfoRoutes } from './modules/clinical-info';
import Assignment from './pages/Assignment/Assignment';
import Layout from './pages/Layout/Layout';
import ProtectedRoute from './utils/Protected/ProtectedRoute';
import PatientMapPage from './pages/PatientMap/PatientMapPage';
import PatientMap from './pages/PatientMap/PatientMap';
import Tools from './pages/Tools/Tools';
import { labPhlebotomyRoutes } from './modules/lab-phlebotomy';
// Legacy imports removed - using modules/specialist-referrals
import ClinicalDataCollectorPage from './pages/ClinicalDataCollector/ClinicalDataCollectorPage';
import ReportPage from './pages/ClinicalDataCollector/ReportPage';
import CoddingOpportunities from './pages/Tools/Apps/Coders/CoddingOpportunities/CoddingOpportunities';
import AddOn from './pages/Tools/Apps/AddOn/AddOn';
import PrePostAudi from './pages/Tools/Apps/Pre&PostAudi/PrePostAudi';
import AHCA from './pages/Tools/Apps/AHCA/AHCA';
import NoRecentVisit from './pages/Tools/Apps/NoRecentVisit/NoRecentVisit';
import PCPChanges from './pages/Tools/Apps/PCPChanges/PCPChanges';
import NewMember from './pages/Tools/Apps/NewMember/NewMember';
import ACAAging6265 from './pages/Tools/Apps/ACAAging6265/ACAAging6265';
import ACAAging65 from './pages/Tools/Apps/ACAAging65/ACAAging65';
import PostVisit from './pages/Tools/Apps/PostVisit/PostVisit';
// Old SpecialistReferals from pages (replaced by module)
// import SpecialistReferals from './pages/Tools/Apps/SpecialistReferals/SpecialistReferals';
import { SpecialistReferralsPage, ReferralCreatePage } from './modules/specialist-referrals';
import WelcomeCampaign2 from './pages/Tools/Apps/WelcomeCampaign/WelcomeCampaign';
import ReportGeneratorPage from './pages/ClinicalDataCollector/ReportGeneratorPage';
import CreateClinicalNote from './pages/CreateClinicalNote/CreateClinicalNote';
import ClinicalNotesList from './pages/ClinicalNotes/ClinicalNotesList';
import MedicationReconciliationPage from './pages/MedicationReconciliation/MedicationReconciliationPage';
import DataQualityValidationPage from './pages/DataQuality/DataQualityValidationPage';
import CareGapsPageContainer from "./modules/care-gaps";
import { ClinicalNoteAiPage } from './modules/clinical-note-ai';
import { CodingOpV3WorklistPage, PatientDetailPage } from "./modules/coding-opportunities";
import CreateClinicalNotePage from './modules/clinical-notes/pages/ClinicalNotesPage';
import AiCodingClinicalNotePage from "./modules/coding-opportunities/clinical-notes/AiCodingClinicalNotePage";
import SupportPage from './modules/support';
import { AdministrationPage } from './modules/administration';
import { generalRoutes } from './modules/general';
import { technicalIssuesRoutes } from './modules/technical-issues';
import { libraryRoutes } from './modules/library';
import Unauthorized from './pages/Unauthorized/Unauthorized';
import { AccessDeniedPage, RoleGuard } from './pages/AccessDenied';


const AppRouter = () => {
    const { loading } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
  
    // Check token validity on route changes
    useEffect(() => {
        const token = localStorage.getItem("token");
        if (!loading && (!token || isTokenExpired(token))) {
          localStorage.removeItem("user");
          localStorage.removeItem("token");
          const publicPaths = ["/Login", "/ChangePassword", "/auth/change-password"];
          if (!publicPaths.includes(location.pathname)) {
            navigate("/Login");
          }
        }
      }, [loading, location.pathname, navigate]);
    
      if (loading) return <LoadingSpinner message="Checking session..." />;
  
    return (
      <PatientModalProvider>
      <ObservationsModalProvider>
        <Routes>
          <Route path="/" element={<Navigate to="/Main" replace />} />
          <Route path="/Login" element={<Login />} />
          <Route path="/auth/change-password" element={<ChangePasswordPage />} />
          <Route path="/ChangePassword" element={<ChangePasswordPage />} />
          <Route path="/Unauthorized" element={<Unauthorized />} />
          <Route path="/AccessDenied" element={<AccessDeniedPage />} />
          {/* Bloqueo HIPAA: usuarios sin 2FA enrolado aterrizan aquí desde
              ProtectedRoute y no pueden acceder al resto de la app hasta
              completar el enrollment. La propia página maneja los guards
              (no-user → /Login, mfaEnabled=true → /Main). */}
          <Route path="/mfa-required" element={<MfaRequiredPage />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>

              {mainRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={<RoleGuard>{r.element}</RoleGuard>} />
              ))}

              {/* <Route path="/Main" element={<Main />} />               */}

              {carePlansRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={<RoleGuard>{r.element}</RoleGuard>} />
              ))}
              <Route path="/profile" element={<RoleGuard><UserProfilePage /></RoleGuard>} />
              <Route path="/patient-profiles" element={<RoleGuard><PatientProfilesPage /></RoleGuard>} />
              {clinicalInfoRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={<RoleGuard>{r.element}</RoleGuard>} />
              ))}
              <Route path="/Assignment" element={<RoleGuard><Assignment /></RoleGuard>} />
              <Route path="/patient-map" element={<RoleGuard><PatientMapPage /></RoleGuard>} />
              <Route path="/PatientMap" element={<RoleGuard><PatientMap /></RoleGuard>} />
              <Route path="/Tools" element={<RoleGuard><Tools /></RoleGuard>} />
              {labPhlebotomyRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={<RoleGuard>{r.element}</RoleGuard>} />
              ))}
              <Route path="/AHCA" element={<RoleGuard><AHCA /></RoleGuard>} />
              <Route path="/PrePostAudi" element={<RoleGuard><PrePostAudi /></RoleGuard>} />
              <Route path="/SpecialistReferralForm" element={<RoleGuard><ReferralCreatePage /></RoleGuard>} />
              <Route path="/NoRecentVisit" element={<RoleGuard><NoRecentVisit /></RoleGuard>} />
              <Route path="/PCPChanges" element={<RoleGuard><PCPChanges /></RoleGuard>} />
              <Route path="/SpecialistReferralDashboard" element={<RoleGuard><SpecialistReferralsPage /></RoleGuard>} />
              <Route path="/specialist-referrals" element={<RoleGuard><SpecialistReferralsPage /></RoleGuard>} />
              <Route path="/specialist-referrals/create" element={<RoleGuard><ReferralCreatePage /></RoleGuard>} />
              <Route path="/specialist-referrals/edit/:referralId" element={<RoleGuard><ReferralCreatePage /></RoleGuard>} />
              <Route path="/ClinicalDataCollectorPage" element={<RoleGuard><ClinicalDataCollectorPage /></RoleGuard>} />
              <Route path="/report" element={<RoleGuard><ReportPage /></RoleGuard>} />
              <Route path="/CoddingOpportunities" element={<RoleGuard><CoddingOpportunities /></RoleGuard>} />
              <Route path="/ACAAging6265" element={<RoleGuard><ACAAging6265 /></RoleGuard>} />
              <Route path="/ACAAging65" element={<RoleGuard><ACAAging65 /></RoleGuard>} />
              <Route path="/AddOn" element={<RoleGuard><AddOn /></RoleGuard>} />
              <Route path="/CareGap" element={<Navigate to="/care-gaps" replace />} />
              <Route path="/WelcomeCampaign" element={<RoleGuard><WelcomeCampaign2 /></RoleGuard>} />
              <Route path="/NewMember" element={<RoleGuard><NewMember /></RoleGuard>} />
              <Route path="/PostVisit" element={<RoleGuard><PostVisit /></RoleGuard>} />
              <Route path="/ReportGeneratorPage" element={<RoleGuard><ReportGeneratorPage /></RoleGuard>} />
              <Route path="/patient-detail/:patientId" element={<RoleGuard><PatientMapPage /></RoleGuard>} />
              <Route path="/care-gaps" element={<RoleGuard><CareGapsPageContainer /></RoleGuard>} />
              <Route path="/CreateClinicalNote" element={<RoleGuard><CreateClinicalNote /></RoleGuard>} />
              <Route path="/ClinicalNotesList" element={<RoleGuard><ClinicalNotesList /></RoleGuard>} />
              <Route path="/MedicationReconciliationPage" element={<RoleGuard><MedicationReconciliationPage /></RoleGuard>} />
              <Route path="/DataQualityValidationPage" element={<RoleGuard><DataQualityValidationPage /></RoleGuard>} />
              <Route path="/ClinicalNoteAi" element={<RoleGuard><ClinicalNoteAiPage /></RoleGuard>} />
              <Route path="/coding-opportunities" element={<RoleGuard><CodingOpV3WorklistPage /></RoleGuard>} />
              <Route path="/coding-opportunities/patient/:cipId" element={<RoleGuard><PatientDetailPage /></RoleGuard>} />
              <Route path="/CreateClinicalNotePage" element={<RoleGuard><CreateClinicalNotePage /></RoleGuard>} />
              <Route path="/coding-opportunities/clinical-notes/ai" element={<RoleGuard><AiCodingClinicalNotePage /></RoleGuard>}/>
              <Route path="/support" element={<RoleGuard><SupportPage /></RoleGuard>} />
              {assignmentRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={<RoleGuard>{r.element}</RoleGuard>} />
              ))}
              {generalRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={<RoleGuard>{r.element}</RoleGuard>} />
              ))}
              {technicalIssuesRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={<RoleGuard>{r.element}</RoleGuard>} />
              ))}
              <Route path="/administration" element={<RoleGuard><AdministrationPage /></RoleGuard>} />
              {libraryRoutes.map((r) => (
                <Route key={r.path} path={r.path} element={<RoleGuard>{r.element}</RoleGuard>} />
              ))}
            </Route>
          </Route>
        </Routes>
      </ObservationsModalProvider>
      </PatientModalProvider>
    );
  };
  
  export default AppRouter;